import { cn } from '@workspace/ui/lib/utils'
import { Check, FileSearch, Lightbulb, Loader2, MessageSquare, TrendingUp } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import type { PreparationStep } from '../types'

const TOTAL_DURATION_SECONDS = 300

interface MilestoneConfig {
  icon: typeof FileSearch
  label: string
  description: string
  step: PreparationStep
  completesAt: number
}

const MILESTONES: MilestoneConfig[] = [
  {
    icon: FileSearch,
    label: 'Analyzing Pitch Deck',
    description: 'Reviewing your presentation and extracting key information',
    step: 'ANALYZING_DECK',
    completesAt: TOTAL_DURATION_SECONDS * 0.1,
  },
  {
    icon: TrendingUp,
    label: 'Researching Market & Competition',
    description: 'Analyzing your market landscape and competitive positioning',
    step: 'RESEARCHING',
    completesAt: TOTAL_DURATION_SECONDS * 0.6,
  },
  {
    icon: MessageSquare,
    label: 'Preparing Questions',
    description: 'Crafting tailored questions for your pitch',
    step: 'PREPARING_QUESTIONS',
    completesAt: TOTAL_DURATION_SECONDS * 1,
  },
]

function stepToIndex(step: PreparationStep | null | undefined): number | null {
  if (!step) return null
  return MILESTONES.findIndex((m) => m.step === step)
}

function formatRemaining(seconds: number): string {
  if (seconds <= 60) return 'less than a minute'
  const m = Math.ceil(seconds / 60)
  return m === 1 ? '1 minute' : `${m} minutes`
}

function getElapsed(createdAt: string | Date): number {
  return Math.max(0, Math.floor((Date.now() - new Date(createdAt).getTime()) / 1000))
}

export interface PitchPendingStateProps {
  preparationStep?: PreparationStep | null
  createdAt: string | Date
}

export function PitchPendingState({ preparationStep, createdAt }: PitchPendingStateProps) {
  const [elapsed, setElapsed] = useState(() => getElapsed(createdAt))

  useEffect(() => {
    const interval = setInterval(() => setElapsed(getElapsed(createdAt)), 1000)
    return () => clearInterval(interval)
  }, [createdAt])

  const remaining = Math.max(0, TOTAL_DURATION_SECONDS - elapsed)
  const stepIndex = stepToIndex(preparationStep)

  const activeIndex = useMemo(() => {
    if (stepIndex !== null && stepIndex >= 0) return stepIndex
    for (let i = MILESTONES.length - 1; i >= 0; i--) {
      const m = MILESTONES[i]
      if (m && elapsed >= m.completesAt) return Math.min(i + 1, MILESTONES.length - 1)
    }
    return 0
  }, [elapsed, stepIndex])

  return (
    <>
      <h1 className="text-2xl font-semibold tracking-tight text-foreground">
        Preparing your pitch
      </h1>
      <p className="mt-1 text-sm text-muted-foreground">
        {remaining > 0 ? (
          <>
            Should be ready in{' '}
            <span className="font-medium text-foreground">{formatRemaining(remaining)}</span>
          </>
        ) : (
          <span className="animate-pulse text-[#f97316]">Almost ready...</span>
        )}
      </p>

      <div className="mt-5 overflow-hidden rounded-xl border border-border bg-card/30">
        <div className="px-5 py-4">
          {MILESTONES.map((milestone, index) => {
            const isCompleted =
              stepIndex !== null ? index < stepIndex : elapsed >= milestone.completesAt
            const isActive = index === activeIndex && !isCompleted
            const isLast = index === MILESTONES.length - 1

            return (
              <div key={milestone.label} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <div
                    className={cn(
                      'flex size-7 shrink-0 items-center justify-center rounded-full transition-all duration-500',
                      isCompleted && 'bg-emerald-400/15 text-emerald-400',
                      isActive && 'bg-[#f97316]/15 text-[#f97316]',
                      !isCompleted && !isActive && 'bg-border/50 text-muted-foreground/40'
                    )}
                  >
                    {isCompleted ? (
                      <Check className="size-3.5" />
                    ) : isActive ? (
                      <Loader2 className="size-3.5 animate-spin" />
                    ) : (
                      <milestone.icon className="size-3.5" />
                    )}
                  </div>
                  {!isLast && (
                    <div
                      className={cn(
                        'w-px flex-1 transition-colors duration-500',
                        isCompleted ? 'bg-emerald-400/30' : 'bg-border/50'
                      )}
                    />
                  )}
                </div>

                <div className={cn('pb-4', isLast && 'pb-0')}>
                  <p
                    className={cn(
                      'text-sm font-medium transition-colors duration-500',
                      isCompleted && 'text-emerald-400',
                      isActive && 'text-foreground',
                      !isCompleted && !isActive && 'text-muted-foreground/40'
                    )}
                  >
                    {milestone.label}
                  </p>
                  <p
                    className={cn(
                      'mt-0.5 text-xs transition-colors duration-500',
                      isActive ? 'text-muted-foreground' : 'text-muted-foreground/40'
                    )}
                  >
                    {milestone.description}
                  </p>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Tip */}
      <div className="mt-4 flex items-start gap-3 rounded-xl border border-amber-400/20 bg-amber-400/[0.04] px-4 py-3">
        <Lightbulb className="mt-0.5 size-4 shrink-0 text-amber-400" />
        <p className="text-xs leading-relaxed text-muted-foreground">
          <span className="font-medium text-amber-400">Tip:</span> When you're done answering, just
          pause. The AI will pick up that you're ready for the next question.
        </p>
      </div>
    </>
  )
}
